import { memo } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/20/solid';

import LessonProgressDashboardStats from '@/models/LessonProgressDashboardStats';
import { useLessonProgressStats } from './LessonProgressStats.hooks';
import {
  IconType,
  IconTypeImport,
} from '@/components/ui/custom/LessonIconContainer';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import { useTranslation } from 'react-i18next';

type Props = Pick<
  LessonProgressDashboardStats,
  | 'activitiesProgress'
  | 'bookProgress'
  | 'challengeProgress'
  | 'hasAccessedTool'
  | 'hasWatchedInstruction'
> & {
  slugCourseName: string;
  lessonId: number;
  klassId?: number;
};

const LessonProgressStats = memo((props: Props) => {
  const {
    activitiesProgress,
    bookProgress,
    challengeProgress,
    hasAccessedTool,
    hasWatchedInstruction,
    slugCourseName,
    lessonId,
    klassId,
  } = props;

  const { stats } = useLessonProgressStats({
    activitiesProgress,
    bookProgress,
    challengeProgress,
    hasAccessedTool,
    hasWatchedInstruction,
    slugCourseName,
    lessonId,
    klassId,
  });

  return (
    <ul className="flex items-center gap-2">
      {stats.map(stat => (
        <StatsMember
          key={stat.id}
          type={stat.id}
          done={stat.done}
          to={stat.to}
        />
      ))}
    </ul>
  );
});

export default LessonProgressStats;

type StatsMemberProps = {
  type: IconType;
  done?: boolean;
  to: string;
};

const StatsMember = memo((props: StatsMemberProps) => {
  const { type, done, to } = props;

  const { t } = useTranslation('translation', {
    keyPrefix: 'klassView.iconType',
  });

  return (
    <Link to={to}>
      <Tooltip text={t(type)}>
        <li className="w-6 relative bg-secondary/25 rounded-full">
          <ConditionalRenderer condition={done}>
            <figure className="absolute z-10 w-full h-full flex items-center justify-center bg-success/80 rounded-full">
              <CheckIcon className="text-base-100" />
            </figure>
          </ConditionalRenderer>
          <figure className="w-full h-full flex items-center justify-center">
            {IconTypeImport[type]}
          </figure>
        </li>
      </Tooltip>
    </Link>
  );
});
